import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, Output, QueryList, SimpleChanges, ViewChildren } from "@angular/core";
import { IVeriTab } from "@directives/_veri/veriTab/veriTab.component";
import { NavigationService } from "@services/navigation.service";
import { VTabComponent } from "./v-tab.component";
import "./v-tabs.component.scss";

export enum VTabStepperState {
    Todo,
    InProgress,
    Complete
}

export interface IDynamicTabDefinition<T = any> {
    title: string;
    count?: number;
    icon?: string;
    data?: T; // do we need?
}

@Component({
    selector: "v-tabs",
    template: require("./v-tabs.component.html")
})
export class VTabsComponent implements AfterViewInit {

    @Input() @HostBinding("class.stepper") stepper: boolean;
    @Input() @HostBinding("class.scroll") scroll: boolean;
    @Input() tabLayout: string = "column";
    @Input() @HostBinding("class.hide-tab-content") hideTabContent: boolean;
    @Input() hideFinalButton: boolean = false;

    @Input() onFinish: string;

    @Input() startTab: string;

    @Output() onFinishCallback = new EventEmitter<void>();
    @Output() tabSelect = new EventEmitter<VTabComponent>();

    // --

    @ViewChildren('tabLink') tabLinks!: QueryList<ElementRef>;

    // ---

    tabs: VTabComponent[] = [];
    selectedTab: VTabComponent;
    nextTab: VTabComponent;

    disablePrev = true;

    cachedTabTitlesKey: string;

    fontAwesome = false;

    underlineLeft = 0;
    underlineWidth = 0;

    get shouldHidePrevButton(): boolean {
        return this.hideFinalButton && this.selectedTab.tabIndex == this.tabs.length - 1;
    }

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly navigationService: NavigationService) {
    }

    ngAfterViewInit(): void {
        this.setDefaultSelectedTab();
        setTimeout(() => { this.updateUnderline() }, 300);
    }

    registerTab(component: VTabComponent): void {
        component.tabIndex = component.order || this.tabs.length;
        component.tabIndex = Math.min(component.tabIndex, this.tabs.length);

        this.tabs.splice(component.tabIndex, 0, component);

        if (component.icon?.indexOf("fa-") == 0) {
            this.fontAwesome = true;
        }

        if (this.tabs?.length > 1 && !this.startTab) {
            return;
        }

        this.setDefaultSelectedTab();
    }

    unregisterTab(component: VTabComponent): void {
        this.tabs = this.tabs.filter(t => t != component);
    }

    selectTab(tab: VTabComponent, $event?: MouseEvent, allowOverride: boolean = false): void {

        // Guard clause for prev / nexdt
        if (!tab || (this.selectedTab?.disablePrev && !allowOverride) || tab.disabled) {
            return;
        }

        tab.selected = null;

        // Set previous tab to Complete
        if (this.selectedTab) {
            this.selectedTab.stepperState = VTabStepperState.Complete;
        }

        this.selectedTab = tab;
        this.tabSelect.emit(tab);

        this.updateUnderline();

        // Stepper logic
        var selectedTabIndex = this.tabs.indexOf(this.selectedTab);

        if (this.selectedTab?.disablePrev) {
            this.tabs.forEach(tab => {
                tab.clickable = false;
            })
        }

        this.tabs.forEach((tab) => {
            tab.hideTab();
        });

        // This is in a timeout to prevent layout shift
        tab.showTab();

        if (tab.stepperState != VTabStepperState.Complete) {
            tab.stepperState = VTabStepperState.InProgress;
        }

        $event?.preventDefault();

        this.disablePrev = tab == this.tabs[0];
    }

    onTabStepperStateChanged(tab: VTabComponent): void {

        if (tab.stepperState == VTabStepperState.Complete) {
            var next = this.getNextTab();
            if (next) {
                this.selectTab(next);
            }
        }
    }

    stepNext(): void {
        if (this.getNextTab()) {
            this.selectTab(this.getNextTab());
            return;
        }

        if (!!this.onFinish) {
            this.navigationService.go(this.onFinish);
            return;
        }

        this.onFinishCallback.emit();
    }

    stepPrev(overrideDisable: boolean = false): void {
        this.selectTab(this.getPrevTab(), null, overrideDisable);
    }

    stepToBeginning(): void {
        this.selectedTab.disablePrev = false;
        this.selectTab(this.tabs[0], null, true);
    }

    trackBy(index: number, tab: IVeriTab): string {
        return tab.tabTitle;
    }

    updateUnderline(): void {
        var selectedTabIndex = this.tabs.indexOf(this.selectedTab);

        if (!this.tabLinks || this.tabLinks?.length == 0) {
            setTimeout(() => {
                this.updateUnderline();
            }, 50);

            return;
        }

        if (this.tabLinks.toArray()[selectedTabIndex]?.nativeElement) {
            const tabLink = this.tabLinks.toArray()[selectedTabIndex].nativeElement;
            this.underlineLeft = tabLink.offsetLeft;
            this.underlineWidth = tabLink.offsetWidth;
        }
    }

    // ---

    private getPrevTab(): VTabComponent {
        var currentIndex = this.tabs.indexOf(this.selectedTab);

        if (currentIndex <= 0) {
            return null;
        }

        return this.tabs[currentIndex - 1];
    }

    private getNextTab(): VTabComponent {
        var currentIndex = this.tabs.indexOf(this.selectedTab);

        return this.tabs[currentIndex + 1];
    }

    private setDefaultSelectedTab(): void {
        if (!this.tabs?.length) {
            return;
        }

        let startTab = this.tabs[0];

        if (this.startTab) {
            startTab = this.tabs.find(tab => tab.tabId == this.startTab);
        }

        this.selectTab(startTab || this.tabs[0]);

        this.cd.detectChanges();
    }
}
